import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    // Helper function to check user access
    const checkUserAccess = async (userId: string) => {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);
      
      return roles && roles.length > 0;
    };

    // 1. Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Only synchronous state updates here
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Defer the Supabase call with setTimeout to prevent deadlock
        setTimeout(async () => {
          const hasAccess = await checkUserAccess(session.user.id);
          setHasAccess(hasAccess);
          setLoading(false);
        }, 0);
      } else {
        setHasAccess(false);
        setLoading(false);
      }
    });

    // 2. THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session }, error: sessionError }) => {
      if (sessionError || !session) {
        setUser(null);
        setHasAccess(false);
        setLoading(false);
        return;
      }

      setUser(session.user);
      const hasAccess = await checkUserAccess(session.user.id);
      setHasAccess(hasAccess);
      setLoading(false);
    }).catch(() => {
      setUser(null);
      setHasAccess(false);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user || !hasAccess) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
